import {
    Badge,
    Modal,
    Stack,
    TextContainer,
    TextStyle,
    Tooltip,
} from "@shopify/polaris";
import { startCase } from "lodash";
import React, { useCallback, useReducer } from "react";
import styled from "styled-components";

import { useAppContext } from "../../../context";
import { arrInvalid, checkRole, getFullName, reducerFn } from "../../../helper";
import { AMAZON_IDEA_STATUS } from "../../../variable";
import ModalImageClaimPolaris from "../../seller/ModalImageClaimPolaris";
import AmazonIdeaFields from "../amazon-idea-field";
import { ProductLink } from "../amazon-idea-table";
import { STATUSES_BADGE, isFieldHasValue, isStatus } from "../helper";
import AmazonIdeaAccept from "./amazon-idea-accept";
import AmazonIdeaReject from "./amazon-idea-reject";
import AmazonIdeaTimeline from "./amazon-idea-timeline";
import DonwloadAllFiles from "../../shared/download-all-files";

export default function AmazonIdeaDetail({ node, open, onClose }) {
    const statusText = STATUSES_BADGE[node?.status];

    const [state, setState] = useReducer(reducerFn, {
        open: false,
        action: null,
        active: false,
    });

    const { currentUser } = useAppContext();
    const { isSeller, isChannelLeader, isChannelSupportLeader } =
        checkRole(currentUser);
    const isLeader = isSeller || isChannelLeader || isChannelSupportLeader;

    const isNeedReview = isStatus(node, [AMAZON_IDEA_STATUS["Need Review"]]);

    let statusBadge = "";
    if (node?.status) {
        statusBadge = (
            <Badge
                status={statusText}
                progress={
                    node.status === AMAZON_IDEA_STATUS.Doing
                        ? "incomplete"
                        : undefined
                }
            >
                {node.status}
            </Badge>
        );

        if (node.status === AMAZON_IDEA_STATUS.Rejected && node.rejectReason) {
            statusBadge = (
                <>
                    <Tooltip content={node.rejectReason}>
                        {" "}
                        {statusBadge}
                    </Tooltip>
                    <div style={{ paddingLeft: ".8rem" }}>
                        Reason:{" "}
                        <TextStyle variation="subdued">
                            {node.rejectReason}
                        </TextStyle>
                    </div>
                </>
            );
        }

        if (node.status === AMAZON_IDEA_STATUS.Denied && node.denyReason) {
            statusBadge = (
                <>
                    <Tooltip content={node.denyReason}> {statusBadge}</Tooltip>
                    <div style={{ paddingLeft: ".8rem" }}>
                        Reason:{" "}
                        <TextStyle variation="subdued">
                            {node.denyReason}
                        </TextStyle>
                    </div>
                </>
            );
        }
    }

    const handleClose = useCallback(() => {
        setState({ open: false, active: false, action: null });
        onClose();
    }, []);

    const handleClick = useCallback(
        (type) => () => {
            setState({ open: true, action: type });
        },
        [],
    );

    let secondaryActions = [];
    let primaryAction = null;
    if (isLeader && isNeedReview && node) {
        secondaryActions = [
            { content: "Reject", onAction: handleClick("reject") },
        ];
        primaryAction = { content: "Accept", onAction: handleClick("accept") };
    }

    const props = {
        node,
        ids: node ? [node.id] : [],
        open: state.open,
        onClose: handleClose,
    };

    const actionMarkup = {
        accept: <AmazonIdeaAccept {...props} />,
        reject: <AmazonIdeaReject {...props} />,
    };

    return (
        <>
            <Modal
                open={open}
                sectioned
                onClose={onClose}
                title={`Amazon Idea Detail ${node.id ? `#${node.id}` : ""}`}
                secondaryActions={secondaryActions}
                {...(primaryAction ? { primaryAction } : {})}
                large
            >
                <TextContainer>
                    <Stack vertical>
                        <Stack distribution="fill">
                            <Stack vertical>
                                <span>
                                    <TextStyle variation="strong">
                                        Title:{" "}
                                    </TextStyle>
                                    {node?.title}
                                </span>
                                <span>
                                    <TextStyle variation="strong">
                                        Type:{" "}
                                    </TextStyle>
                                    {node?.type}
                                </span>

                                <KeyWrapper>
                                    <TextStyle variation="strong">
                                        Keyword:{" "}
                                    </TextStyle>

                                    {node?.keyword ? (
                                        <p>{node?.keyword || ""}</p>
                                    ) : null}
                                    {node?.keyword2 ? (
                                        <p>{node?.keyword2 || ""}</p>
                                    ) : null}
                                    {node?.keyword3 ? (
                                        <p>{node?.keyword3 || ""}</p>
                                    ) : null}
                                    {node?.keyword4 ? (
                                        <p>{node?.keyword4 || ""}</p>
                                    ) : null}
                                    {node?.keyword5 ? (
                                        <p>{node?.keyword5 || ""}</p>
                                    ) : null}
                                </KeyWrapper>
                                <div>
                                    <Stack>
                                        <TextStyle variation="strong">
                                            Mockups:{" "}
                                        </TextStyle>
                                        {node?.mockups?.length > 0 ? (
                                            <DonwloadAllFiles
                                                files={node.mockups}
                                                filename={node.id || ""}
                                                label="Download all Mockups"
                                            />
                                        ) : null}
                                    </Stack>
                                    {node?.mockups?.length > 0 ? (
                                        <ModalImageClaimPolaris
                                            files={{
                                                files: node.mockups,
                                            }}
                                            limit={1}
                                            width={80}
                                            height={80}
                                            label="Mockups"
                                        />
                                    ) : null}
                                </div>
                                {node?.asin || node?.productLink ? (
                                    <div>
                                        <TextStyle variation="strong">
                                            Product:{" "}
                                        </TextStyle>
                                        <ProductLink
                                            productLink={node?.productLink}
                                            asin={node?.asin}
                                        />
                                    </div>
                                ) : null}
                            </Stack>
                            <Stack vertical>
                                <span>
                                    <TextStyle variation="strong">
                                        KPI:{" "}
                                    </TextStyle>
                                    {node?.kpi}
                                </span>
                                <span>
                                    <TextStyle variation="strong">
                                        Status:{" "}
                                    </TextStyle>
                                    {/* <Badge status={statusText}>
                                    {node?.status || ""}
                                </Badge> */}
                                    {statusBadge}
                                </span>
                                <span>
                                    <TextStyle variation="strong">
                                        Author:{" "}
                                    </TextStyle>
                                    {getFullName(node?.author || {})}
                                </span>
                                {!!node?.picker ? (
                                    <span>
                                        <TextStyle variation="strong">
                                            Picker:{" "}
                                        </TextStyle>
                                        {getFullName(node?.picker || {})}
                                    </span>
                                ) : null}
                            </Stack>
                        </Stack>
                        <span>
                            <TextStyle variation="strong">
                                Bullet Point:{" "}
                            </TextStyle>
                            {node?.bulletPoint}
                        </span>
                        <span>
                            <TextStyle variation="strong">
                                Item Highlight:{" "}
                            </TextStyle>
                            {node?.description}
                        </span>
                        <span>
                            <TextStyle variation="strong">
                                Personalized:{" "}
                            </TextStyle>
                            {node?.isPersonalized ? (
                                <Badge status="success">Yes</Badge>
                            ) : (
                                <Badge status="critical">No</Badge>
                            )}
                        </span>
                        {node?.fields?.length > 0 ? (
                            // <DataTable
                            //     rows={getFieldRows(node.fields) || []}
                            //     headings={["Type", "Label", "Value", "Placeholder"]}
                            //     columnContentTypes={Array.from({
                            //         length: 4,
                            //     }).fill("text")}
                            //     verticalAlign="middle"
                            //     hideScrollIndicator
                            // />
                            <AmazonIdeaFields
                                fields={node.fields}
                                setState={() => {}}
                                disabled
                            />
                        ) : null}

                        <AmazonIdeaTimeline node={node} />
                    </Stack>
                </TextContainer>
            </Modal>
            {actionMarkup[state.action]}
        </>
    );
}

function convertFields(fields) {
    if (arrInvalid(fields)) return fields;

    const newFields = fields.slice();
    newFields.sort((a, b) => a.ordering - b.ordering);
    return newFields.map((field) => {
        if (isFieldHasValue(field.type)) {
            const value = (field.value || "").split(",").filter(Boolean);
            field.value = value;
        }

        field.type = startCase(field.type);

        return { ...field };
    });
}

function getFieldRows(fields) {
    const newFields = convertFields(fields);

    const res = [];
    for (let item of newFields) {
        if (!item) continue;

        const { type, value, label, placeholder } = item;
        const row = [
            <span>{type}</span>,
            <span>{label}</span>,
            <span>
                {!arrInvalid(value) ? (
                    <ul>
                        {value.map((val, idx) => (
                            <li key={`${idx}_${val}`}>
                                <span>{val}</span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    value
                )}
            </span>,
            <span>{placeholder}</span>,
        ];

        res.push(row);
    }

    return res;
}

const KeyWrapper = styled.div`
    p {
        padding-left: 1rem;
    }

    p + p {
        margin-top: 1rem;
    }
`;
